import { useEffect, useState } from 'react';
import { Button } from '@m/alchemy-ui/Button';
import { Toast } from '@m/alchemy-ui/Toast';
import { Tooltip } from '@m/alchemy-ui/Tooltip';
import { Dialog } from '@m/alchemy-ui/Dialog';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import server from 'common/utils/server';

import {
  RewardTierConfiguration,
  SnappyIntegrationConfig
} from 'settings/Settings.types';

import {
  useToasts,
  generateAutoDismissMessage,
  UserInputToastMessage
} from 'settings/hooks/useToasts';
import { useSelector } from 'react-redux';
import { useRewards } from 'settings/hooks/useRewards';
import { RewardsPicker } from 'settings/components/RewardsPicker';
import { RewardsThreshold } from 'settings/components/RewardsThreshold';
import { RewardsStarCount } from 'settings/components/RewardsStarCount';
import { ReactHookFormError } from 'common/components/ReactHookFormError';
import { isSnappyIntegrationActive } from 'settings/utils/snappyIntegration';
import { ConnectWithSnappyDialog } from 'settings/components/ConnectWithSnappyDialog';
import { useConnectWithSnappyDialog } from 'settings/hooks/useConnectWithSnappyDialog';
import { RewardsSnappyIntegration } from 'settings/components/RewardsSnappyIntegration';

import * as Styled from './Rewards.styled';
import { areThresholdsActive, areThresholdsValid } from './Rewards.utils';

type RewardInputs = {
  rewards: RewardTierConfiguration[];
  thresholdEnabled: boolean;
  fiveStarRewards: boolean;
  snappyIntegration: SnappyIntegrationConfig;
};

type RewardsFormProps = {
  data: Omit<ReturnType<typeof useRewards>, 'loading' | 'save' | 'saving'>;
  onSubmit: SubmitHandler<RewardInputs>;
  isLoading: boolean;
  showMessage: (newMessage: UserInputToastMessage) => void;
};

function RewardsForm({
  data,
  onSubmit,
  isLoading,
  showMessage,
}: RewardsFormProps) {
  const { isVisible, closeDialog, dontShowAgain, setDontShowAgain } =
    useConnectWithSnappyDialog(data.snappyIntegration);

  const { handleSubmit, control, watch, setValue, formState, trigger } =
    useForm<RewardInputs>({
      defaultValues: {
        rewards: data.rewards,
        thresholdEnabled: data.thresholdEnabled,
        fiveStarRewards: data.fiveStarRewards,
        snappyIntegration: data.snappyIntegration,
      },
      mode: 'onChange',
    });

  const [clearedAtByTier, setClearedAtByTier] = useState(data.clearedAtByTier);

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const openConfirmDialog = () => setIsConfirmDialogOpen(true);
  const closeConfirmDialog = () => setIsConfirmDialogOpen(false);

  const thresholdEnabled = watch('thresholdEnabled');
  const snappyIntegration = watch('snappyIntegration');
  const multiRegionSnappyRewardsOn = useSelector(
    state => state.companyFeatures.multi_region_snappy_rewards
  );

  const useStellaRewards = () => {
    closeDialog();
    setValue('snappyIntegration', { active: false, apiKey: null });
  };

  const useSnappyRewards = () => {
    closeDialog();
    setValue('snappyIntegration', { active: true, apiKey: null });
  };

  useEffect(() => {
    trigger('thresholdEnabled');
  }, [trigger]);

  const handleClearProgress = () => {
    server
      .patch('rewards/clear_progress', null, { format: 'json' })
      .then(({ message, clearedDates }) => {
        setClearedAtByTier(clearedDates);
        showMessage(
          generateAutoDismissMessage({
            placement: 'top',
            level: 'confirm',
            content: message,
          })
        );
      });

    closeConfirmDialog();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="rewards"
        control={control}
        rules={{
          required: true,
          validate: (value: RewardInputs['rewards']) =>
            areThresholdsValid(value, thresholdEnabled),
        }}
        render={({ field }) => (
          <>
            <RewardsPicker
              value={field.value}
              onChange={field.onChange}
              thresholdEnabled={thresholdEnabled}
              snappyEnabled={isSnappyIntegrationActive(snappyIntegration)}
              multiRegionSnappyRewardsOn={multiRegionSnappyRewardsOn}
              clearedAtByTier={clearedAtByTier}
            />
            <ReactHookFormError control={control} name="rewards" />
          </>
        )}
      />

      <Controller
        name="thresholdEnabled"
        control={control}
        rules={{
          validate: (value: RewardInputs['thresholdEnabled']) =>
            areThresholdsActive(value, snappyIntegration),
        }}
        render={({ field }) => {
          return (
            <Styled.FormElement>
              <RewardsThreshold value={field.value} onChange={field.onChange} />
              <ReactHookFormError control={control} name="thresholdEnabled" />
            </Styled.FormElement>
          );
        }}
      />

      <Controller
        name="fiveStarRewards"
        control={control}
        render={({ field }) => (
          <Styled.FormElement>
            <RewardsStarCount value={field.value} onChange={field.onChange} />
          </Styled.FormElement>
        )}
      />

      <Controller
        name="snappyIntegration"
        control={control}
        render={({ field }) => (
          <Styled.FormElement>
            <RewardsSnappyIntegration
              value={field.value}
              onChange={newConfig => {
                field.onChange(newConfig);
                trigger('thresholdEnabled');
              }}
            />
          </Styled.FormElement>
        )}
      />

      <Styled.FormFooter>
        {thresholdEnabled && (
          <Tooltip
            trigger={
              <Styled.ClearButton
                priority="secondary"
                onClick={() => openConfirmDialog()}
              >
                Clear Rewards Progress
              </Styled.ClearButton>
            }
            placement="top"
            contents="Clearing Reward Progress will set all reward vote progress back to zero for each Team Member. Cleared Reward Progress cannot be retrieved."
          />
        )}
        <Button
          type="submit"
          isLoading={isLoading}
          disabled={!formState.isValid}
        >
          Save
        </Button>
      </Styled.FormFooter>

      <ConnectWithSnappyDialog
        isOpen={isVisible}
        onAccept={useSnappyRewards}
        onDismiss={useStellaRewards}
        onClose={closeDialog}
        dontShowAgain={dontShowAgain}
        onDontShowAgainChange={setDontShowAgain}
      />

      <Dialog
        isOpen={isConfirmDialogOpen}
        onClose={closeConfirmDialog}
        headerContent="Are you sure?"
        bodyContent="Are you sure you want to clear rewards progress?"
        footerButtons={[
          {
            label: 'Cancel',
            onClick: closeConfirmDialog,
            priority: 'tertiary',
          },
          { label: 'Confirm', onClick: handleClearProgress },
        ]}
      />
    </form>
  );
}

export function Rewards() {
  const { messages, showMessage } = useToasts();
  const { save, saving, loading, ...formFields } = useRewards();

  const successSavingMessage = generateAutoDismissMessage({
    placement: 'top',
    level: 'confirm',
    content: 'Selection saved.',
  });

  const errorSavingMessage = generateAutoDismissMessage({
    placement: 'top',
    level: 'error',
    content: 'There was an error saving the selection. Please try again.',
  });

  if (loading) return null;

  const handleSubmit = (newConfiguration: RewardInputs) => {
    save(newConfiguration)
      .then(() => showMessage(successSavingMessage))
      .catch(() => showMessage(errorSavingMessage));
  };

  return (
    <>
      <Toast messages={messages} />
      <RewardsForm
        data={formFields}
        onSubmit={handleSubmit}
        isLoading={saving}
        showMessage={showMessage}
      />
    </>
  );
}
